import { useEffect, useMemo, useState } from "react";
import {
  getFunnelMetrics,
  getPipelineDistribution,
} from "services/funnel-analytics.service";
import {
  ChartSkeleton,
  EmptyMessage,
  ErrorBanner,
} from "./AnalyticsStates";
import FunnelChartCard from "./FunnelChartCard";
import { getMentorshipPipelineDistribution } from "../utils/funnelVisualizationUtils";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";

const FUNNEL_SUBTITLES = {
  main_offer: "VSL Opt-In Page → Sales Page → Checkout Page → Thank You Page",
  blueprint: "Sales Page → Checkout Page → Thank You Page",
  mentorship: "Opt-In Page → VSL Page → Book Call Page → Confirmation Page",
  inner_circle: "Sales Page → Checkout Page → Thank You Page",
  academy: "Home Page → Thank You Page",
};

export default function FunnelVisualization({
  api,
  filters,
  embedded = false,
  funnelId = null,
  hideComparison = false,
  metrics: metricsProp,
  metricsLoading: metricsLoadingProp,
  metricsError: metricsErrorProp,
  pipelineDistribution: pipelineDistributionProp,
  pipelineDistributionLoading: pipelineDistributionLoadingProp,
  pipelineDistributionError: pipelineDistributionErrorProp,
}) {
  const hasExternalMetrics = metricsLoadingProp !== undefined;
  const hasExternalDistribution = pipelineDistributionLoadingProp !== undefined;
  const [internalMetrics, setInternalMetrics] = useState(null);
  const [internalDistribution, setInternalDistribution] = useState([]);
  const [internalMetricsLoading, setInternalMetricsLoading] = useState(true);
  const [internalDistributionLoading, setInternalDistributionLoading] = useState(true);
  const [internalMetricsError, setInternalMetricsError] = useState(null);
  const [internalDistributionError, setInternalDistributionError] = useState(null);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      if (!hasExternalMetrics) {
        setInternalMetricsLoading(false);
        setInternalMetrics(null);
        setInternalMetricsError(null);
      }
      if (!hasExternalDistribution) {
        setInternalDistributionLoading(false);
        setInternalDistribution([]);
        setInternalDistributionError(null);
      }
      return;
    }

    if (hasExternalMetrics && hasExternalDistribution) {
      return;
    }

    let cancelled = false;

    const loadDistribution = async () => {
      setInternalDistributionLoading(true);
      setInternalDistributionError(null);
      try {
        const distributionResult = await getPipelineDistribution(api, { filters });
        if (!cancelled) setInternalDistribution(distributionResult?.distribution ?? []);
      } catch {
        if (!cancelled) {
          setInternalDistribution([]);
          setInternalDistributionError("Failed to load funnel visualization data.");
        }
      } finally {
        if (!cancelled) setInternalDistributionLoading(false);
      }
    };

    const loadInternal = async () => {
      setInternalMetricsLoading(true);
      if (!hasExternalDistribution) {
        setInternalDistributionLoading(true);
      }
      setInternalMetricsError(null);
      if (!hasExternalDistribution) {
        setInternalDistributionError(null);
      }
      try {
        const requests = [getFunnelMetrics(api, { filters })];
        if (!hasExternalDistribution) {
          requests.push(getPipelineDistribution(api, { filters }));
        }
        const results = await Promise.all(requests);
        if (!cancelled) {
          setInternalMetrics(results[0]);
          if (!hasExternalDistribution) {
            setInternalDistribution(results[1]?.distribution ?? []);
          }
        }
      } catch {
        if (!cancelled) {
          setInternalMetrics(null);
          if (!hasExternalDistribution) {
            setInternalDistribution([]);
          }
          setInternalMetricsError("Failed to load funnel visualization data.");
        }
      } finally {
        if (!cancelled) {
          setInternalMetricsLoading(false);
          if (!hasExternalDistribution) {
            setInternalDistributionLoading(false);
          }
        }
      }
    };

    if (hasExternalMetrics) {
      if (!hasExternalDistribution) {
        loadDistribution();
      }
    } else {
      loadInternal();
    }

    return () => {
      cancelled = true;
    };
  }, [api, filters?.dateRangeType, filters?.customDateRange?.[0], filters?.customDateRange?.[1], hasExternalMetrics, hasExternalDistribution]);

  const metrics = hasExternalMetrics ? metricsProp : internalMetrics;
  const metricsLoading = hasExternalMetrics ? metricsLoadingProp : internalMetricsLoading;
  const metricsError = hasExternalMetrics ? metricsErrorProp : internalMetricsError;
  const distribution = hasExternalDistribution ? (pipelineDistributionProp ?? []) : internalDistribution;
  const distributionLoading = hasExternalDistribution
    ? pipelineDistributionLoadingProp
    : internalDistributionLoading;
  const distributionError = hasExternalDistribution
    ? pipelineDistributionErrorProp
    : internalDistributionError;
  const isLoading = metricsLoading || distributionLoading;
  const error = metricsError || distributionError;

  const funnels = metrics?.funnelVisualizations ?? [];
  const targetFunnelId = funnelId ?? (embedded ? "main_offer" : null);
  const visibleFunnels = targetFunnelId
    ? funnels.filter((funnel) => funnel.id === targetFunnelId)
    : funnels;
  const mentorshipStages = useMemo(
    () => getMentorshipPipelineDistribution(distribution),
    [distribution]
  );

  const singleFunnel = Boolean(targetFunnelId);
  const funnelGridClass = singleFunnel
    ? "grid grid-cols-1 gap-4"
    : "grid grid-cols-1 lg:grid-cols-3 gap-6";
  const skeletonHeight = singleFunnel ? 320 : 420;
  const skeletonCount = singleFunnel ? 1 : 3;

  if (isLoading) {
    return (
      <section className={embedded ? "" : "mb-8"}>
        {!embedded && !funnelId && (
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-indigo-500" />
            Funnel Visualization
          </h2>
        )}
        <div className={funnelGridClass}>
          {[...Array(skeletonCount)].map((_, i) => (
            <ChartSkeleton key={i} height={skeletonHeight} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className={embedded ? "" : "mb-8"}>
      {!embedded && !funnelId && !hideComparison && (
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-indigo-500" />
            Funnel Visualization
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Step counts, conversion %, and drop-off %
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4">
          <ErrorBanner message={error} />
        </div>
      )}

      {isCustomRangeIncomplete(filters) ? (
        <EmptyMessage message="Select start and end dates to view funnel data." />
      ) : visibleFunnels.length === 0 ? (
        <EmptyMessage message="No funnel data available." />
      ) : (
        <div className={funnelGridClass}>
          {visibleFunnels.map((funnel) => (
            <FunnelChartCard
              key={funnel.id}
              funnel={funnel}
              subtitle={FUNNEL_SUBTITLES[funnel.id]}
              pipelineStages={
                funnel.id === "mentorship" ? mentorshipStages : undefined
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}
